import Tooltip from 'components/UI/Tooltip';
import { FC, ReactNode, useState, useCallback } from 'react';
import {
  Root,
  Image,
  ButtonsWrapper,
  InputWrapper,
  ColumnInput,
  ColumnTitle,
  FourColumnLayout,
  ModalContent,
} from './styled';
import InputField from 'components/UI/InputField';
import { DesiredValue, Range } from 'models';
import { FieldErrors, FieldValues } from 'react-hook-form';
import { validateType } from 'utils/connectivity';
import IconButton from 'components/UI/IconButton';
import desiredIcon from 'assets/vectors/desire.svg';
import closeIcon from 'assets/vectors/close-icon-active.svg';
import { useIntl, FormattedMessage } from 'react-intl';
import EditModal from 'components/UI/EditModal';
import Button from 'components/UI/Button';
import { ButtonGrid } from 'styles';
import Checkbox from 'components/UI/Checkbox';
import { CheckboxItem } from 'components/connectivity/EditDeviceModal/styled';

type Props = {
  id: string;
  children: ReactNode;
  value?: string;
  title?: string;
  itemKey?: string;
  writable?: boolean;
  type?: string;
  range?: Range;
  register?: any;
  desired?: DesiredValue;
  errors?: FieldErrors<FieldValues>;
  onDesiredRemoved?: (id: string, resourceId: string) => void;
};

// 36 to make a UUID fit. If longer tool tip used
const maxValueLength = 36;

const EditDesiredContent: FC<Props> = ({
  id,
  children,
  value,
  title,
  itemKey,
  writable,
  type,
  range,
  register,
  desired,
  errors,
  onDesiredRemoved,
}) => {
  const intl = useIntl();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleUndesiredClick = useCallback(() => {
    setShowConfirmation(true);
  }, []);

  const handleConfirmUndesired = useCallback(() => {
    if (desired?.resourceId) {
      onDesiredRemoved?.(id, desired.resourceId);
    }
    setShowConfirmation(false);
  }, [id, desired?.resourceId, onDesiredRemoved]);

  const handleCancelUndesired = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  if (!value) {
    return <Root>{children}</Root>;
  }

  const longValue = value.length > maxValueLength;
  const hasDesiredActions = Boolean(desired && writable);

  return (
    <>
      <FourColumnLayout>
        <ColumnTitle>
          <div>{title}</div>
        </ColumnTitle>

        <ColumnInput>
          {longValue ? (
            <Tooltip paragraph={value}>
              {value.substring(0, 15).concat('...')}
            </Tooltip>
          ) : (
            value.charAt(0) === '"' ? value.slice(1, -1) : value
          )}
        </ColumnInput>

        <ColumnInput>
          {writable && (
            <InputWrapper>
              <InputField
                description="connectivity.desired_value"
                defaultValue={desired?.value}
                disabled={false}
                tight={true}
                error={
                  errors
                    ? errors[itemKey || title || '']
                      ? 'Invalid value'
                      : undefined
                    : undefined
                }
                register={register(itemKey || '', {
                  validate: {
                    validateType: (v: any) => {
                      if (v === '' || v === undefined || v === null)
                        return true;
                      return validateType(v, type || '', range);
                    },
                  },
                })}
              />

              {
                // Special for DataControl. Make it possible to set value on resource
                // instead of resource instance since some products (ex batteries) 
                // can't write to resource instances
                title === 'LevelValue' && (<CheckboxItem>
                  <label htmlFor={'special'}>
                    <FormattedMessage id="connectivity.desired_datacontrol_special" />
                  </label>
                  <Checkbox
                    id="excludeTestDeviceBox"
                    defaultChecked={false}
                    register={register('DataControlSpecialCheckbox')}
                  />
                </CheckboxItem>)}
            </InputWrapper>
          )}
        </ColumnInput>

        <ColumnInput>
          {hasDesiredActions && (
            <ButtonsWrapper>
              <Tooltip
                position="top"
                paragraph={`${intl.formatMessage({
                  id: 'connectivity.desired_remove_btn',
                })}`}
              >
                <IconButton onClick={handleUndesiredClick}>
                  <Image src={closeIcon} alt="Clear" />
                </IconButton>
              </Tooltip>
              <Tooltip
                paragraph={`${intl.formatMessage({
                  id: 'connectivity.desired_value',
                })}: ${desired?.value}
        ${intl.formatMessage({
                  id: 'connectivity.desired_status',
                })}: ${typeof desired?.failed === 'number' && desired.failed > 15
                  ? 'Failed'
                  : 'Active'
                  }
        ${intl.formatMessage({ id: 'connectivity.desired_set_by' })}: ${desired?.setBy || 'N/A'
                  }`}
              >
                <IconButton>
                  <Image src={desiredIcon} alt="Desired" />
                </IconButton>
              </Tooltip>
            </ButtonsWrapper>
          )}
        </ColumnInput>
      </FourColumnLayout>

      {/* Confirmation Modal */}
      <EditModal
        title="connectivity.desired_remove_btn"
        open={showConfirmation}
        close={handleCancelUndesired}
      >
        <ModalContent>
          <FormattedMessage
            id="connectivity.desired_remove_confirm"
            values={{
              value: <strong>{desired?.value}</strong>,
              resource: <strong>{desired?.resourceId}</strong>,
            }}
          />
        </ModalContent>
        <ButtonGrid>
          <Button
            onClick={handleCancelUndesired}
            backgroundColor="transparent"
            color="primary"
          >
            <FormattedMessage id="device_desired_values.btn_cancel" />
          </Button>
          <Button onClick={handleConfirmUndesired} id="confirm_undesired_btn">
            <FormattedMessage id="device_desired_values.btn_confirm" />
          </Button>
        </ButtonGrid>
      </EditModal>
    </>
  );
};

export default EditDesiredContent;
